import { createContext, useContext } from 'react'

import pathMappings from '../../pathMapping.json'

const CATEGORIES = ['wheels', 'rims', 'tyres'] as const

/** Route key of a category search, as useLocalizedPath resolves its public url from. */
export type SeoSearchCategory = (typeof CATEGORIES)[number]

/**
 * Root url of each category, in both locales. The Swedish urls are read from
 * pathMapping.json rather than written out, so renaming a slug there carries through
 * instead of silently leaving every page under the old prefix without a search; the
 * English url is the route key itself.
 */
const CATEGORY_BY_ROOT_URL: Record<string, SeoSearchCategory> = Object.fromEntries(
  CATEGORIES.flatMap((category) => [
    [`/${pathMappings[category].sv}`, category],
    [`/${category}`, category],
  ])
)

/** Brand pages sit outside the category tree, so their search has to be named per brand. */
const CATEGORY_BY_BRAND: Record<string, SeoSearchCategory> = {
  blackbear: 'tyres',
  ridgeblade: 'tyres',
  'boost-wheels': 'rims',
  'dirt-at-wheels': 'rims',
}

const BRAND_ROOTS: string[] = [`/${pathMappings.brands.sv}`, '/brands']

/**
 * Which category search an SEO page offers above its copy, or null for none.
 *
 * A root gets none: the three category roots already carry their real search above the
 * copy, and the brand overview spans both rims and tyres at once. Neither does an faq
 * page, which answers questions rather than leads to a dimension.
 */
export const seoSearchCategory = (url: string): SeoSearchCategory | null => {
  const root = `/${url.split('/')[1]}`

  if (BRAND_ROOTS.includes(root)) {
    if (url === root) return null
    return CATEGORY_BY_BRAND[url.split('/').pop() ?? ''] ?? null
  }

  if (url === root || url === `${root}/faq`) return null

  return CATEGORY_BY_ROOT_URL[root] ?? null
}

/**
 * Carries the category from the page down to the heading override that renders the search.
 * A context rather than props: MDXProvider memoises on the identity of the component map
 * it is handed, so a per-page map would invalidate the whole MDX subtree on every render.
 */
export const SeoSearchCategoryContext = createContext<SeoSearchCategory | null>(null)

export const useSeoSearchCategory = () => useContext(SeoSearchCategoryContext)
